/**
 * CarteGriseCard — Displays carte grise info with inline status/location editing
 */
import React, { useState } from 'react';
import { FileText, MapPin, AlertTriangle, Check, Edit2, X } from 'lucide-react';
import api from '../../../api.js';
import { useFormat } from '../../../i18n/format.js';

const STATUS_CONFIG = {
  original: { label: 'Original', color: 'bg-green-100 text-green-800' },
  duplicate: { label: 'Duplicata', color: 'bg-blue-100 text-blue-800' },
  pending_transfer: { label: 'Transfert en cours', color: 'bg-amber-100 text-amber-800' },
  transferred: { label: 'Transféré', color: 'bg-gray-100 text-gray-600' },
  pending_new: { label: 'En attente (nouveau)', color: 'bg-amber-100 text-amber-800' },
  lost: { label: 'Perdue', color: 'bg-red-100 text-red-800' },
};

const LOCATION_CONFIG = {
  office: { label: 'Au bureau', icon: '🏢' },
  with_driver: { label: 'Avec le conducteur', icon: '👤' },
  prefecture: { label: 'En préfecture', icon: '🏛️' },
  leasing_company: { label: 'Chez le loueur', icon: '🏦' },
  garage: { label: 'Au garage', icon: '🔧' },
  other: { label: 'Autre', icon: '📍' },
};

function CarteGriseCard({ vehicle, onUpdate, onMessage }) {
  const { fmt } = useFormat();
  const [editing, setEditing] = useState(false);
  const [editStatus, setEditStatus] = useState(vehicle?.carte_grise_status || 'original');
  const [editLocation, setEditLocation] = useState(vehicle?.carte_grise_location || 'office');
  const [editLocationDetail, setEditLocationDetail] = useState(vehicle?.carte_grise_location_detail || '');
  const [saving, setSaving] = useState(false);

  const hasData = vehicle?.carte_grise_number || vehicle?.carte_grise_date || vehicle?.carte_grise_holder;
  const status = STATUS_CONFIG[vehicle?.carte_grise_status] || STATUS_CONFIG.original;
  const location = LOCATION_CONFIG[vehicle?.carte_grise_location] || LOCATION_CONFIG.office;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.vehicle.update(vehicle.id, {
        carte_grise_status: editStatus,
        carte_grise_location: editLocation,
        carte_grise_location_detail: editLocationDetail || null,
      });
      onMessage?.({ title: 'Succès', message: 'Carte grise mise à jour', type: 'success' });
      setEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Carte grise update error:', error);
      onMessage?.({ title: 'Erreur', message: 'Impossible de mettre à jour la carte grise', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Alert banner for problematic statuses
  const showAlert = vehicle?.carte_grise_status === 'lost' || vehicle?.carte_grise_status === 'pending_transfer' || vehicle?.carte_grise_status === 'pending_new';

  return (
    <div className="space-y-4">
      {showAlert && (
        <div className={`flex items-center gap-2 p-3 rounded-lg ${
          vehicle.carte_grise_status === 'lost' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
        }`}>
          <AlertTriangle className={`w-4 h-4 ${vehicle.carte_grise_status === 'lost' ? 'text-red-600' : 'text-amber-600'}`} />
          <p className={`text-sm font-medium ${vehicle.carte_grise_status === 'lost' ? 'text-red-800' : 'text-amber-800'}`}>
            {vehicle.carte_grise_status === 'lost'
              ? 'Carte grise déclarée perdue — demande de duplicata nécessaire'
              : vehicle.carte_grise_status === 'pending_transfer'
              ? 'Transfert de carte grise en cours'
              : 'Nouvelle carte grise en attente'
            }
          </p>
        </div>
      )}

      {/* Carte Grise fields — standard French format with field codes */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">A · Immatriculation</p>
          <p className="font-mono font-bold text-gray-900">{vehicle?.license_plate || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">B · 1re immatriculation</p>
          <p className="text-gray-900">{vehicle?.first_registration_date ? fmt.date(vehicle.first_registration_date) : '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">C.1 · Titulaire</p>
          <p className="text-gray-900 truncate">{vehicle?.carte_grise_holder || vehicle?.owner_name || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">D.1 · Marque</p>
          <p className="text-gray-900">{vehicle?.make || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">D.2 · Type / Modèle</p>
          <p className="text-gray-900 truncate">{vehicle?.model || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">E · VIN</p>
          <p className="font-mono text-xs text-gray-700 truncate">{vehicle?.vin || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">P.3 · Énergie</p>
          <p className="text-gray-900">{{ gasoline: 'Essence', diesel: 'Diesel', electric: 'Electrique', hybrid: 'Hybride' }[vehicle?.fuel_type] || vehicle?.fuel_type || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">P.6 · Puiss. fiscale</p>
          <p className="text-gray-900">{vehicle?.puissance_fiscale ? `${vehicle.puissance_fiscale} CV` : '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">V.7 · CO2</p>
          <p className="text-gray-900">{vehicle?.co2_gkm ? `${vehicle.co2_gkm} g/km` : '-'}</p>
        </div>
      </div>

      {/* Formule number + Crit'Air (document-specific data) */}
      <div className="flex items-center gap-4 pt-2 border-t border-gray-100 mt-2 text-sm">
        <div className="flex-1">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">N° de formule</p>
          <p className="font-mono text-gray-700 text-xs">{vehicle?.carte_grise_number || <span className="italic text-gray-400">-</span>}</p>
        </div>
        <div className="flex-1">
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Date émission CG</p>
          <p className="text-gray-700 text-xs">{vehicle?.carte_grise_date ? fmt.date(vehicle.carte_grise_date) : <span className="italic text-gray-400">-</span>}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Crit'Air</p>
          <p className="text-gray-900 font-medium">{vehicle?.crit_air_category || '-'}</p>
        </div>
      </div>

      {/* Status + Location row */}
      {!editing ? (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Statut</p>
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${status.color}`}>
                {status.label}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Localisation</p>
              <span className="text-sm text-gray-900">
                {location.icon} {location.label}
                {vehicle?.carte_grise_location_detail && (
                  <span className="text-gray-500 ml-1">({vehicle.carte_grise_location_detail})</span>
                )}
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              setEditStatus(vehicle?.carte_grise_status || 'original');
              setEditLocation(vehicle?.carte_grise_location || 'office');
              setEditLocationDetail(vehicle?.carte_grise_location_detail || '');
              setEditing(true);
            }}
            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100"
            title="Modifier statut et localisation"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Localisation</label>
              <select
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(LOCATION_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Précision</label>
            <input
              type="text"
              value={editLocationDetail}
              onChange={(e) => setEditLocationDetail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Bureau de M. Dupont, Préfecture de Lyon..."
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X className="w-3 h-3 inline mr-1" /> Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Check className="w-3 h-3 inline mr-1" /> Enregistrer
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default CarteGriseCard;
